import React from "react";
import { Alert, Descriptions, Modal, Space, Tag, Typography } from "antd";
import { canGeneratePaymentLink, resolveSubscriptionCustomer } from "../utils/payments";

//
// Confirmation step before a payment link is issued.
//
// It shows what the backend is about to bill, which matters because the
// request itself carries nothing but two ids: the administrator is confirming
// the plan and subscription, and the amounts shown here are the server's own
// figures for that plan, not a calculation this component performed.
//

const resolvePlanName = (subscription, plans) =>
  subscription?.plan?.name ||
  plans?.find((plan) => plan.id === subscription?.plan_id)?.name ||
  "Unknown plan";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

const GeneratePaymentLinkModal = ({
  open,
  subscription,
  customers = [],
  plans = [],
  linkExpiryMinutes,
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const customer = resolveSubscriptionCustomer(subscription, customers);
  const planName = resolvePlanName(subscription, plans);
  const eligibility = canGeneratePaymentLink(subscription);
  const customerEmail = String(customer?.email || "").trim();

  const expiryDescription = (() => {
    if (!Number.isFinite(Number(linkExpiryMinutes))) return "Set by the server";

    const minutes = Number(linkExpiryMinutes);
    if (minutes % 1440 === 0) {
      const days = minutes / 1440;
      return `${days} day${days === 1 ? "" : "s"}`;
    }
    if (minutes % 60 === 0) {
      const hours = minutes / 60;
      return `${hours} hour${hours === 1 ? "" : "s"}`;
    }
    return `${minutes} minutes`;
  })();

  return (
    <Modal
      open={open}
      destroyOnHidden
      title="Generate Payment Link"
      onCancel={() => {
        if (!loading) onCancel?.();
      }}
      onOk={onConfirm}
      okText="Generate &amp; Email Link"
      okButtonProps={{ loading, disabled: !eligibility.allowed || !customerEmail }}
      cancelButtonProps={{ disabled: loading }}
      width={560}
    >
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        {!eligibility.allowed && (
          <Alert type="error" showIcon message={eligibility.reason} />
        )}

        {/* Only shown when it is actually true, and it blocks the action:
            a link with nowhere to go is not worth generating. */}
        {eligibility.allowed && !customerEmail && (
          <Alert
            type="warning"
            showIcon
            message="This customer has no email address. Add one before generating a link."
          />
        )}

        {/* Two columns so six facts take three rows rather than six. */}
        <Descriptions
          size="small"
          column={2}
          bordered
          items={[
            { key: "customer", label: "Customer", children: customer?.name || "Unknown customer" },
            {
              key: "email",
              label: "Email",
              children: customerEmail || <Tag color="red">None on file</Tag>,
            },
            { key: "plan", label: "Plan", children: planName },
            {
              key: "status",
              label: "Status",
              children: <Tag>{subscription?.status || "—"}</Tag>,
            },
            {
              key: "period",
              label: "Current period",
              children: `${formatDate(subscription?.start_date)} – ${formatDate(subscription?.end_date)}`,
            },
            { key: "expiry", label: "Link valid", children: expiryDescription },
          ]}
        />

        {/* The subscription list carries no plan price, so there is no
            authoritative figure to show here -- saying so plainly beats an
            estimate the customer could be charged differently for. Compressed
            to one line because it is context, not a warning. */}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Amount, tax and billing period are calculated by the server and shown on the payment
          record. A UPI QR code (valid up to 2 hours) is emailed alongside the link. The
          subscription renews only after a payment is verified.
        </Typography.Text>
      </Space>
    </Modal>
  );
};

export default GeneratePaymentLinkModal;
